import React, { useMemo } from 'react';

import { OfferType } from 'constants/enums';
import { CircleFadingPlus } from 'lucide-react';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import { OfferPageLayout } from '../../components/OfferPageLayout';
import { useOfferTable } from '../../hooks/useOfferTable';
import { OffersFilterId } from '../../OfferFilters.types';

import { CheatsDialog } from './components/CheatsDialog';
import { useBuyOneGetManyCheats } from './hooks/useBuyOneGetManyCheats';
import { useBuyOneGetManyColumns } from './hooks/useBuyOneGetManyColumns';

export const BuyOneGetManyTable = (): React.ReactElement => {
  const {
    getOffers,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    sorting,
    updateSorting,
    hasActiveFilters,
    permissions,
    canManageOffers,
    canUserEdit,
    actions,
    selectedOffer,
    isDeleteDialogOpen,
    showLimitedEditModal,
    handleDeleteOffer,
    handleCreate,
    handleCloseLimitedEditModal,
    handleCloseDeleteDialog,
    updateCollectedIndex,
    offers,
    processOffers
  } = useOfferTable({
    offerType: OfferType.BUY_ONE_GET_MANY,
    offerLabel: '1+Many',
    needsPricings: false
  });

  const {
    cheatsOffer,
    cheatsOfferId,
    hasFeatureFlagCheats,
    onOpenCheats,
    handleCloseCheats
  } = useBuyOneGetManyCheats();

  let cheatsProductsSequence: NonNullable<
    typeof cheatsOffer
  >['productsSequence'] = [];
  if (cheatsOffer !== null) {
    cheatsProductsSequence = cheatsOffer.productsSequence;
  }

  const { columns } = useBuyOneGetManyColumns({
    sorting,
    updateSorting,
    isLoading: getOffers.isLoading,
    actions,
    permissions,
    hasFeatureFlagCheats,
    onOpenCheats
  });

  const processed = useMemo(
    () => processOffers(offers),
    [offers, processOffers]
  );

  return (
    <OfferPageLayout
      title="1+Many"
      buttonText={processed.buttonText}
      onCreateClick={handleCreate}
      canUserEdit={canUserEdit}
      canManageOffers={canManageOffers}
      offerLabel="1+Many"
      showFilters={offers.length > 0}
      hasActiveFilters={hasActiveFilters}
      filterBarConfig={{
        hiddenFilters: [OffersFilterId.OFFER_DESIGN, OffersFilterId.PRICE]
      }}
      offers={offers}
      displayData={processed.sorted}
      columns={columns}
      totalCount={processed.totalCount}
      totalPages={processed.totalPages}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      isLoading={getOffers.isLoading}
      emptyStateTitle="Add your first 1+Many offer"
      emptyStateDescription={
        <>
          1+Many offers reward a paid purchase with one or more free tiles
          <br />
          unlocked alongside the main offer.
        </>
      }
      createButtonPrefix="1+Many"
      bodyDataTestId="dashBuyOneGetManyList"
      emptyStateIcon={
        <CircleFadingPlus className="text-base-mutedForeground h-6 w-6" />
      }
      offerType={OfferType.BUY_ONE_GET_MANY}
      offerDesignType={OfferDesignTypes.BuyOneGetMany}
      selectedOffer={selectedOffer}
      showLimitedEditModal={showLimitedEditModal}
      onCloseLimitedEditModal={handleCloseLimitedEditModal}
      isDeleteDialogOpen={isDeleteDialogOpen}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onConfirmDelete={handleDeleteOffer}
      deleteModalTitle="Delete 1+Many offer"
      deleteModalDescription="Are you sure you want to delete this 1+Many offer?"
    >
      <CheatsDialog
        cheatsOfferId={cheatsOfferId}
        productsSequence={cheatsProductsSequence}
        updateCollectedIndex={updateCollectedIndex}
        onClose={handleCloseCheats}
      />
    </OfferPageLayout>
  );
};
